// NPM imports
import React, { Component } from "react";
import { connect } from "react-redux";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  NumberInput,
  Tooltip,
  Button,
  Label,
  Input,
} from "../../../revvComponents";
import { numberForamting } from "../../../utils/globalUtils";

// Components Imports
import BundleRow from "../BundleRow";

// Actions Imports
import PricebooksActions from "../../../actions/PricebooksActions";

// Service Imports
import { searchProfessionalServices } from "../../../actions/PricebooksActions/pricebooksService";

// Internal Imports
import { PRICEBOOK } from "../../../constants";

// Destructuring
const { updatePricingMethodData } = PricebooksActions;
const { BUNDLE_TYPES } = PRICEBOOK;

// Declarations
const ADHOC = "adhoc";
const BUNDLE = "bundle";
const NON_LINEAR = "non-linear";

class BundleEditor extends Component {
  searchDropDownRef = React.createRef();

  // Make the Search API on 1 sec timeInterval
  delaySearch = debounce(searchTxt => {
    this._onSearch(searchTxt);
  }, 1000);

  componentDidMount() {
    if (this.props.isEdit) {
      this._updateState({
        showSearch: false,
      });
    }
  }

  _updateState = newState => {
    this.props.updatePricingMethodData(BUNDLE, newState);
  };

  _handleSearchInputClick = () => {
    // attach/remove event handler
    if (!this.props.data.showSuggestion) {
      document.addEventListener("click", this._handleOutsideClick, false);
    } else {
      document.removeEventListener("click", this._handleOutsideClick, false);
    }

    this._updateState({
      showSuggestion: !this.props.data.showSuggestion,
    });
  };

  _handleOutsideClick = e => {
    const node = this.searchDropDownRef.current;
    // ignore clicks on the component itself
    if (node && node.contains(e.target)) {
      return;
    }
    this._handleSearchInputClick();
  };

  _onBasePriceChange = value => {
    const listPrice = this._returnListPrice(
      this.props.data.bundles,
      parseFloat(value),
    );
    this._updateState({ basePrice: value, value: listPrice });
  };

  _onValueChange = (type, index, value) => {
    const cloneBundles = [...this.props.data.bundles];
    cloneBundles[index][type] = value;
    const listPrice = this._returnListPrice(
      cloneBundles,
      this.props.data.basePrice,
    );
    this._updateState({ bundles: cloneBundles, value: listPrice });
  };

  _onBundleTypeChange = value => {
    this._updateState({ bundleType: value, isAdhoc: value === ADHOC });
  };

  _deleteEntry = index => {
    const cloneBundles = [...this.props.data.bundles];
    cloneBundles.splice(index, 1);
    const listPrice = this._returnListPrice(
      cloneBundles,
      this.props.data.basePrice,
    );
    this._updateState({
      bundles: cloneBundles,
      value: listPrice,
      products: [],
      searchTxt: "",
    });
  };

  _onSearch = searchTxt => {
    searchProfessionalServices(this.props.metaData.pricelistId, searchTxt)
      .then(res => {
        this._updateState({ products: res });
      })
      .catch(error => console.error(error));
  };

  _onSearchTxtChange = event => {
    event.persist();
    this._updateState({ searchTxt: event.target.value });
    this.delaySearch(event.target.value);
  };

  _onProductSelect = data => {
    const cloneState = { ...this.props.data };
    const obj = {
      id: data.id,
      months: "",
      price: data.price.value,
      sku: data.product.name,
    };
    cloneState.bundles.push(obj);
    const listPrice = this._returnListPrice(
      cloneState.bundles,
      this.props.data.basePrice,
    );
    this._updateState({
      bundles: cloneState.bundles,
      value: listPrice,
      searchTxt: "",
      showSearch: false,
    });
  };

  _returnListPrice = items =>
    items.reduce((sumValue, item) => sumValue + item.price * item.months, 0); // (Price * Months ) in a loop

  _toggleSearch = () => {
    this._updateState({
      showSearch: !this.props.data.showSearch,
      products: [],
    });
  };

  _isBtnDisabled = () => {
    const { bundles, isAdhoc, showSearch } = this.props.data;
    let isDisabled = true;
    if (
      bundles.length !== 0 &&
      !showSearch &&
      (isAdhoc || (!isAdhoc && bundles.every(bundle => bundle.months !== "")))
    ) {
      isDisabled = false;
    }
    return isDisabled;
  };

  _decideView = () => {
    if (this.props.data.products.length > 0) {
      return this.props.data.products.map(product => (
        <div
          className="bundleeditor-suggestion-item"
          key={product.product.name}
          onClick={() => this._onProductSelect(product)}
          onKeyPress={() => this._onProductSelect(product)}
          role="button"
          tabIndex="0">
          {product.product.name}
        </div>
      ));
    }
    return (
      <div className="bundleeditor-search-empty">
        Start typing to see suggestions
      </div>
    );
  };

  getTotalSku = (value, basePrice, currencySymbol) =>
    numberForamting(value + (basePrice || 0), currencySymbol);

  render() {
    const {
      currencySymbol,
      data: {
        basePrice,
        bundleType,
        bundles,
        searchTxt,
        showSearch,
        showSuggestion,
        value,
      },
    } = this.props;
    return (
      <div className="bundleeditor-wrapper">
        <section className="bundleeditor-types-wrapper">
          <div
            className="bundleeditor-radio-buttons"
            onChange={event => this._onBundleTypeChange(event.target.value)}
            data-test="bundleeditor-div">
            {BUNDLE_TYPES.map(type => (
              <Label
                className="bundleeditor-individual-radio"
                htmlFor={`${type.alias}-type`}
                key={type.alias}>
                <Input
                  checked={bundleType === type.alias}
                  id={`${type.alias}-type`}
                  name={type.alias}
                  readOnly
                  type="radio"
                  value={type.alias}
                />
                <div className="bundleeditor-title-wrapper">
                  <span className="bundleeditor-title">
                    {type.label}
                    {type.alias !== "basic" && (
                      <Tooltip
                        placement="top"
                        align={{
                          offset: [0, 4],
                        }}
                        overlay={type.description}>
                        <span className="whats-this">
                          <FontAwesomeIcon icon={["far", "question-circle"]} />
                        </span>
                      </Tooltip>
                    )}
                  </span>
                </div>
              </Label>
            ))}
          </div>
        </section>
        <section className="bundleeditor-baseprice-wrapper">
          <div>
            <div className="bundleeditor-title">
              Base Price
              <Tooltip
                placement="top"
                align={{
                  offset: [0, 4],
                }}
                overlay="This field can be used to add platform fee or base price of any other kind. The total value will have this price added.">
                <span className="whats-this">
                  <FontAwesomeIcon icon={["far", "question-circle"]} />
                </span>
              </Tooltip>
            </div>
            <NumberInput
              className="bundleeditor-baseprice"
              inpCurrencySymbol={currencySymbol}
              isCurrency
              isFloat
              onChange={this._onBasePriceChange}
              placeholder="Enter Base Price"
              value={basePrice}
              data-test="bundleeditor-number-input"
            />
          </div>
        </section>
        <section>
          <div className="bundleeditor-subtitle-wrapper">
            <span className="bundleeditor-subtitle is-first">SKU</span>
            <span className="bundleeditor-subtitle">Unit Price</span>
            <span className="bundleeditor-subtitle is-last">Scale</span>
          </div>
          {bundles.map((product, index) => (
            <BundleRow
              currencySymbol={currencySymbol}
              data={product}
              deleteEntry={this._deleteEntry}
              index={index}
              // eslint-disable-next-line react/no-array-index-key
              key={`${product.sku}-${index}`}
              onChange={this._onValueChange}
              totalEntries={bundles.length - 1}
              type={bundleType}
              data-test="bundleeditor-bundlerow"
            />
          ))}
          {(bundles.length === 0 || showSearch) && (
            <div className="bundleeditor-search-wrapper">
              <Input
                className="bundleeditor-input is-first"
                type="text"
                value={searchTxt}
                placeholder="Search"
                onChange={this._onSearchTxtChange}
                onClick={this._handleSearchInputClick}
                data-test="bundleeditor-input"
              />
              <div className="bundleeditor-input is-disabled">Unit Price</div>
              <div className="bundleeditor-input is-last is-disabled">
                Effort (in person-month)
              </div>
              {showSuggestion && (
                <div
                  className="bundleeditor-search-suggestion"
                  ref={this.searchDropDownRef}>
                  {this._decideView()}
                </div>
              )}
            </div>
          )}
          <div>
            <Button
              disabled={this._isBtnDisabled()}
              icon={["far", "plus-circle"]}
              onClick={this._toggleSearch}
              data-test="bundleeditor-addsku-btn">
              ADD SKU
            </Button>
          </div>
        </section>
        {bundleType !== NON_LINEAR && (
          <section className="dealtable-pricing-preview">
            <div className="pricing-preview-title">Price Preview</div>
            <div className="pricing-preview-total">
              Total resource value is{" "}
              <b>{numberForamting(value, currencySymbol)}</b>, base price is{" "}
              <b>{numberForamting(basePrice, currencySymbol)}</b>, then total
              value of SKU is{" "}
              <b>
                {this.getTotalSku(
                  parseFloat(value, 10),
                  parseFloat(basePrice, 10),
                  currencySymbol,
                )}
              </b>
              {/* Need to minus with to handle hanging declimal value i.e 123. */}
            </div>
          </section>
        )}
      </div>
    );
  }
}

const mapStateToProps = store => {
  const {
    pricingMethodData: { bundle: data } = {}, // Targed nested keys and set use a specific name for it => priceingMethodData.bundle = data
    metaData,
  } = store.PricebookStore;
  return {
    data,
    metaData,
  };
};

const mapDispatchToProps = {
  updatePricingMethodData,
};

export default connect(mapStateToProps, mapDispatchToProps)(BundleEditor);
